import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { formatIDR, fmtDate } from "@/lib/constants";

/**
 * Drawer "Penelusuran Ketidakpastian" — read-only terhadap perhitungan.
 * Membuka drawer, mencari, memfilter, atau mencentang item TIDAK mengubah
 * status maupun perhitungan. Aksi Verifikasi (hanya user berwenang) memakai
 * alur Verifikasi 1 resmi (POST /petty-cash/{id}/verify-item) per item dan
 * tercatat terpisah di Audit Log.
 */

const CAT_KEYS = ["menunggu_verifikasi", "status_tidak_selaras", "dokumen_belum_terverifikasi", "konflik_nominal"];
const CAT_STATUS = {
  menunggu_verifikasi: "Menunggu Verifikasi 1",
  status_tidak_selaras: "Tidak Selaras",
  dokumen_belum_terverifikasi: "Belum Lolos Verifikasi 1",
  konflik_nominal: "Konflik Nominal",
};
const PAGE_SIZE = 25;

// Badge ringkas Penyebab Konflik; penjelasan lengkap di tooltip (title).
function causeBadge(r, fallback) {
  const st = r.item_status;
  if (st === "Ditolak") return { label: "Ditolak di V1", cls: "bg-rose-100 text-rose-800 border-rose-300" };
  if (st === "Belum terverifikasi") return { label: "Belum Lolos V1", cls: "bg-amber-100 text-amber-800 border-amber-300" };
  if (st === "Diubah setelah verifikasi") return { label: "Diubah Pasca-V1", cls: "bg-blue-100 text-blue-800 border-blue-300" };
  if (fallback === "Konflik Nominal") return { label: "Konflik Nominal", cls: "bg-violet-100 text-violet-800 border-violet-300" };
  if (fallback === "Menunggu Verifikasi") return { label: "Menunggu Verifikasi 1", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  if (fallback === "Belum Terverifikasi") return { label: "Dok. Belum V1", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: fallback || "—", cls: "bg-slate-100 text-slate-600 border-slate-300" };
}

export default function DrillDownDrawer({ drill, report, onClose, canVerify, onVerifyItem, onVerifyItems }) {
  const dd = report?.drilldown || {};
  const isCatMode = !!drill?.category;
  const [active, setActive] = useState(drill?.category || CAT_KEYS[0]);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkReason, setBulkReason] = useState("");
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fProject, setFProject] = useState("");
  const [fPeriode, setFPeriode] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => { if (drill?.category) setActive(drill.category); }, [drill]);
  // Reset pilihan & halaman saat ganti kategori, filter, pencarian, atau data berubah.
  useEffect(() => { setSelected(new Set()); setPage(0); }, [active, search, fStatus, fProject, fPeriode, report]);

  // Semua hook WAJIB sebelum early return (rules-of-hooks).
  const catData = isCatMode ? (dd[active] || { label: active, count: 0, total: 0, rows: [] }) : null;
  const rows = isCatMode ? (catData?.rows || []) : (drill?.rows || []);
  const statusOf = (r) => r.item_status || r.status_label || drill?.defaultStatus || (isCatMode ? CAT_STATUS[active] : null);
  const statusOptions = useMemo(() => [...new Set(rows.map(statusOf).filter(Boolean))], [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const projectOptions = useMemo(() => [...new Set(rows.map((r) => r.project_name).filter(Boolean))], [rows]);
  const periodeOptions = useMemo(() => [...new Set(rows.map((r) => r.week_label).filter(Boolean))], [rows]);

  if (!drill) return null;

  const handleVerify = async (r) => {
    const key = `${r.pc_id}:${r.item_id}`;
    setBusyIds((s) => new Set(s).add(key));
    try {
      await onVerifyItem(r.pc_id, r.item_id);
    } catch {
      // toast error sudah ditampilkan di parent
    } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  // Tombol verifikasi: hanya kategori yang memang butuh Verifikasi 1 dan baris
  // per-item (item_id ada). Item ditolak menunggu revisi pengaju — tanpa tombol.
  const canVerifyRow = (r) => (
    !!canVerify && isCatMode
    && (active === "menunggu_verifikasi" || active === "status_tidak_selaras")
    && !!r.pc_id && !!r.item_id
    && r.item_status !== "Ditolak"
  );
  const colCount = canVerify ? 12 : 10;

  const staffName = report?.scope?.user_name || report?.user?.name || "Tidak tersedia";
  const title = isCatMode ? "Penelusuran Ketidakpastian" : (drill.title || "Rincian Pembentuk");

  const cell = (v) => (v === null || v === undefined || v === "" ? <span className="text-slate-400 italic">Tidak tersedia</span> : v);
  const tanggalOf = (r) => r.filled_at || r.verified_at || r.transfer_date || r.return_date || r.transferred_at || r.created_at || null;
  const nominalOf = (r) => r.actual_amount ?? r.amount ?? r.transfer_amount ?? r.base_transfer_amount ?? null;
  const causeText = (r) => r.alasan || r.reject_note || null;

  // Pencarian + filter (status/proyek/periode). Murni tampilan — data sumber utuh.
  const q = search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (fStatus && statusOf(r) !== fStatus) return false;
    if (fProject && (r.project_name || "") !== fProject) return false;
    if (fPeriode && (r.week_label || "") !== fPeriode) return false;
    if (!q) return true;
    const hay = [r.name, r.item_name, r.project_name, r.week_label, causeText(r), r.verified_by, r.pc_id]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
  const filteredTotal = filtered.reduce((a, r) => a + (nominalOf(r) ?? 0), 0);
  const docCount = new Set(filtered.map((r) => r.pc_id).filter(Boolean)).size;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const rowKey = (r) => `${r.pc_id}:${r.item_id}`;
  const eligibleRows = filtered.filter(canVerifyRow);
  const allSelected = eligibleRows.length > 0 && eligibleRows.every((r) => selected.has(rowKey(r)));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(eligibleRows.map(rowKey)));
  const toggleOne = (r) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(rowKey(r))) n.delete(rowKey(r)); else n.add(rowKey(r));
    return n;
  });
  const selectedRows = rows.filter((r) => selected.has(rowKey(r)));
  const selectedTotal = selectedRows.reduce((a, r) => a + (nominalOf(r) ?? 0), 0);

  const submitBulk = async () => {
    if (!bulkReason.trim()) return;
    setBulkBusy(true);
    try {
      await onVerifyItems(selectedRows, bulkReason.trim());
      setSelected(new Set());
      setConfirmBulk(false);
      setBulkReason("");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="drilldown-drawer" onClick={onClose}>
      <div
        className="bg-white w-full max-w-6xl h-full overflow-y-auto border-l border-slate-300 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
          <div>
            <h2 className="font-display text-[13px] text-[#0B1D3A]">{title}</h2>
            <div className="text-[10px] text-slate-500">
              Membuka drawer tidak mengubah perhitungan. Aksi <b>Verifikasi</b> (hanya user berwenang)
              memakai alur Verifikasi 1 resmi (verifikasi pengeluaran) dan tercatat di Audit Log.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200" data-testid="drilldown-close"><X size={14} /></button>
        </div>

        {isCatMode && (
          <div className="flex gap-1 px-4 pt-3 flex-wrap" data-testid="drilldown-cats">
            {CAT_KEYS.map((ck) => {
              const c = dd[ck] || { label: ck, count: 0, total: 0 };
              return (
                <button
                  key={ck}
                  onClick={() => setActive(ck)}
                  data-testid={`drilldown-cat-${ck}`}
                  className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider border text-left ${active === ck ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                >
                  <div className="font-semibold">{c.label || ck}</div>
                  <div className={`tabular-nums ${active === ck ? "text-slate-300" : "text-slate-500"}`}>
                    {c.count} item · {formatIDR(c.total || 0)}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="p-4">
          {/* Pencarian + filter */}
          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="drilldown-filters">
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari item / proyek / periode / verifikator…"
                className="pl-7 pr-2 py-1 border border-slate-300 text-[11px] w-64"
                data-testid="drilldown-search"
              />
            </div>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border border-slate-300 text-[11px] px-1.5 py-1" data-testid="drilldown-filter-status">
              <option value="">Semua Status</option>
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={fProject} onChange={(e) => setFProject(e.target.value)} className="border border-slate-300 text-[11px] px-1.5 py-1" data-testid="drilldown-filter-project">
              <option value="">Semua Proyek</option>
              {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={fPeriode} onChange={(e) => setFPeriode(e.target.value)} className="border border-slate-300 text-[11px] px-1.5 py-1" data-testid="drilldown-filter-periode">
              <option value="">Semua Periode</option>
              {periodeOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {(search || fStatus || fProject || fPeriode) && (
              <button
                onClick={() => { setSearch(""); setFStatus(""); setFProject(""); setFPeriode(""); }}
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-rose-600"
                data-testid="drilldown-filter-reset"
              >Reset</button>
            )}
          </div>

          {/* Hubungan data + ringkasan kategori */}
          <div className="text-[10px] text-slate-500 mb-2" data-testid="drilldown-summary">
            {isCatMode ? `${catData.label} — ${catData.count} item · total ${formatIDR(catData.total || 0)}` : `${rows.length} baris · total ${formatIDR(rows.reduce((a, r) => a + (nominalOf(r) ?? 0), 0))}`}
            <span className="ml-2 text-slate-600" data-testid="drilldown-doc-relation">
              · {filtered.length} rincian berasal dari {docCount} dokumen · nominal terfilter {formatIDR(filteredTotal)}
            </span>
          </div>

          {/* Bar aksi massal */}
          {canVerify && isCatMode && eligibleRows.length > 0 && (
            <div className="flex items-center justify-between gap-2 mb-2 border border-emerald-200 bg-emerald-50 px-2 py-1.5" data-testid="drilldown-bulk-bar">
              <span className="text-[11px] text-emerald-900">
                <b>{selected.size}</b> dari {eligibleRows.length} item dipilih — setiap item diverifikasi satu per satu via alur resmi dan tercatat terpisah di Audit Log.
              </span>
              <button
                onClick={() => setConfirmBulk(true)}
                disabled={bulkBusy || selected.size === 0}
                className="px-3 py-1 text-[10px] uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 whitespace-nowrap"
                data-testid="drilldown-verify-selected"
              >{bulkBusy ? "Memproses…" : `Verifikasi 1 Terpilih (${selected.size})`}</button>
            </div>
          )}

          <div className="overflow-auto border border-slate-200">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50">
                <tr>
                  {canVerify && (
                    <th className="px-2 py-1.5 border-b border-slate-200 w-8">
                      {eligibleRows.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          data-testid="drilldown-select-all"
                          title="Pilih semua item terfilter yang dapat diverifikasi"
                        />
                      )}
                    </th>
                  )}
                  {["Staf", "Proyek", "Periode", "Tanggal", "Nominal", "Status Realisasi", "Status Dokumen", "Penyebab Konflik", "Verifikator", "Data Sumber", ...(canVerify ? ["Aksi"] : [])].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 border-b border-slate-200 whitespace-nowrap text-[10px] uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr><td colSpan={colCount} className="px-3 py-6 text-center text-slate-400 italic" data-testid="drilldown-empty">Tidak ada data pada kategori/filter ini.</td></tr>
                )}
                {pageRows.map((r, i) => {
                  const badge = causeBadge(r, isCatMode ? CAT_STATUS[active] : drill.defaultStatus);
                  const full = causeText(r);
                  return (
                    <tr key={`${r.pc_id || "x"}-${r.item_id || i}`} className="border-b border-slate-100" data-testid={`drilldown-row-${i}`}>
                      {canVerify && (
                        <td className="px-2 py-1.5">
                          {canVerifyRow(r) && (
                            <input
                              type="checkbox"
                              checked={selected.has(`${r.pc_id}:${r.item_id}`)}
                              onChange={() => toggleOne(r)}
                              disabled={bulkBusy}
                              data-testid={`drilldown-select-${i}`}
                            />
                          )}
                        </td>
                      )}
                      <td className="px-2 py-1.5">{cell(r.user_name || staffName)}</td>
                      <td className="px-2 py-1.5">{cell(r.project_name)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{cell(r.week_label)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{tanggalOf(r) ? fmtDate(tanggalOf(r)) : cell(null)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{nominalOf(r) != null ? formatIDR(nominalOf(r)) : cell(null)}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{cell(statusOf(r))}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{cell(r.doc_status || r.kind)}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${badge.cls}`}
                          title={full || badge.label}
                          data-testid={`drilldown-cause-${i}`}
                        >{badge.label}</span>
                      </td>
                      <td className="px-2 py-1.5">{cell(r.verified_by)}</td>
                      <td className="px-2 py-1.5">
                        {r.pc_id ? (
                          <Link
                            to={`/petty-cash?open=${r.pc_id}`}
                            className="text-[#FF5A1F] hover:underline text-[10px] uppercase tracking-wider whitespace-nowrap"
                            data-testid={`drilldown-open-${i}`}
                          >Buka Data Sumber</Link>
                        ) : <span className="text-slate-400 italic text-[10px]">Tidak tersedia</span>}
                      </td>
                      {canVerify && (
                        <td className="px-2 py-1.5">
                          {canVerifyRow(r) && (
                            <button
                              onClick={() => handleVerify(r)}
                              disabled={busyIds.has(`${r.pc_id}:${r.item_id}`)}
                              className="px-2 py-0.5 border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-[10px] uppercase tracking-wider whitespace-nowrap disabled:opacity-50"
                              data-testid={`drilldown-verify-${i}`}
                              title="Sahkan pengeluaran item ini melalui Verifikasi 1 (tercatat di Audit Log)"
                            >
                              {busyIds.has(`${r.pc_id}:${r.item_id}`) ? "Memproses…" : "Verifikasi 1"}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-2 text-[11px] text-slate-600" data-testid="drilldown-pagination">
              <span>Menampilkan {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} dari {filtered.length} rincian</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-2 py-0.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
                  data-testid="drilldown-page-prev"
                >Sebelumnya</button>
                <span className="px-2 py-0.5 tabular-nums">{page + 1}/{pageCount}</span>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  className="px-2 py-0.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"
                  data-testid="drilldown-page-next"
                >Berikutnya</button>
              </div>
            </div>
          )}

          <div className="text-[10px] text-slate-400 mt-2 italic">
            Setelah konflik diselesaikan dari data sumber, metrik dihitung ulang otomatis dan label "Belum dapat dipastikan" hilang bila tidak ada masalah tersisa.
          </div>
        </div>
      </div>

      {/* Dialog konfirmasi Verifikasi Terpilih — wajib alasan */}
      {confirmBulk && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="drilldown-bulk-confirm" onClick={() => !bulkBusy && setConfirmBulk(false)}>
          <div className="bg-white w-full max-w-md border border-slate-300 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-display text-[13px] text-[#0B1D3A]">Konfirmasi Verifikasi 1 Terpilih</h3>
              <button onClick={() => setConfirmBulk(false)} disabled={bulkBusy} className="p-1 hover:bg-slate-200" data-testid="drilldown-bulk-cancel-x"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3 text-[11px]">
              <div className="border border-amber-200 bg-amber-50 p-2 text-amber-900">
                Anda akan meloloskan <b>{selected.size} item</b> dengan total nominal <b>{formatIDR(selectedTotal)}</b> melalui
                <b> Verifikasi 1</b> (verifikasi pengeluaran). Setiap item diproses satu per satu via alur resmi
                dan dicatat terpisah di Audit Log. Hanya pengguna berwenang yang dapat melakukan ini.
              </div>
              <div className="max-h-28 overflow-auto border border-slate-200">
                <table className="w-full text-[10px]">
                  <tbody>
                    {selectedRows.slice(0, 8).map((r, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-2 py-1">{r.item_name || r.name || "Tidak tersedia"}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{formatIDR(nominalOf(r) ?? 0)}</td>
                      </tr>
                    ))}
                    {selectedRows.length > 8 && (
                      <tr><td colSpan={2} className="px-2 py-1 text-slate-400 italic">…dan {selectedRows.length - 8} item lainnya</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Alasan Verifikasi <span className="text-rose-600">*</span>
                </label>
                <textarea
                  rows={2}
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  className="w-full border border-slate-300 px-2 py-1 text-[11px]"
                  placeholder="Mis. Bukti nota sudah dicek ulang terhadap mutasi rekening"
                  data-testid="drilldown-bulk-reason"
                />
              </div>
            </div>
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button
                onClick={() => setConfirmBulk(false)}
                disabled={bulkBusy}
                className="px-3 py-1 text-xs border border-slate-300 hover:bg-slate-100"
                data-testid="drilldown-bulk-cancel"
              >Batal</button>
              <button
                onClick={submitBulk}
                disabled={bulkBusy || !bulkReason.trim()}
                className="px-3 py-1 text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                data-testid="drilldown-bulk-submit"
              >{bulkBusy ? "Memproses…" : `Proses Verifikasi 1 (${selected.size} item)`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
